import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchFeatures } from '../api';
import { DEFAULT_FEATURES, normalizeFeatures } from './featureFlags';

const FeaturesContext = createContext({
  features: DEFAULT_FEATURES,
  isLoadingFeatures: false,
  isFeaturesLoaded: false,
  refreshFeatures: async () => {}
});

/**
 * Состав включённых интеграций. В отличие от TimeZoneProvider запрос уходит только после
 * логина: до него /settings/features вернёт 401 и глобальный обработчик выкинет из приложения.
 *
 * Дефолт — «выключено»: пока флаг не загружен, iiko-блоки не рендерим, иначе на нересторанном
 * тенанте они мигали бы при каждой загрузке страницы.
 */
const FeaturesProvider = ({ isAuthenticated = false, children }) => {
  const [features, setFeatures] = useState(DEFAULT_FEATURES);
  const [isLoadingFeatures, setIsLoadingFeatures] = useState(false);
  // Отдельно от isLoadingFeatures: до первого эффекта тот ещё false, а флаги — лишь умолчания.
  const [isFeaturesLoaded, setIsFeaturesLoaded] = useState(false);

  const refreshFeatures = useCallback(async () => {
    if (!isAuthenticated) {
      setFeatures(DEFAULT_FEATURES);
      return;
    }

    setIsLoadingFeatures(true);
    try {
      const result = await fetchFeatures();
      setFeatures(normalizeFeatures(result));
    } catch (error) {
      console.warn('Failed to load feature flags.', error);
      setFeatures(DEFAULT_FEATURES);
    } finally {
      setIsLoadingFeatures(false);
      setIsFeaturesLoaded(true);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refreshFeatures();
  }, [refreshFeatures]);

  const value = useMemo(
    () => ({ features, isLoadingFeatures, isFeaturesLoaded, refreshFeatures }),
    [features, isLoadingFeatures, isFeaturesLoaded, refreshFeatures]
  );

  return <FeaturesContext.Provider value={value}>{children}</FeaturesContext.Provider>;
};

const useFeatures = () => useContext(FeaturesContext);

export { FeaturesContext, FeaturesProvider, useFeatures };
